import React, { useState, useEffect, useCallback } from 'react';
import { AdminService } from '../../services/AdminService';

// Activity log interface
interface ActivityLog {
  _id: string;
  logType: 'security' | 'system';
  eventType: string;
  userId?: string;
  userEmail?: string;
  description: string;
  metadata?: any;
  timestamp: string;
  ipAddress?: string;
  userAgent?: string;
}

interface ActivityLogsResponse {
  success: boolean;
  data: {
    logs: ActivityLog[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      pages: number;
    };
  };
  message?: string; // Optional error message field
}

/**
 * ActivityLogs - Component for viewing and managing system activity logs
 * Allows admins to filter by log type, event type, user, and date range
 */
const ActivityLogs: React.FC = () => {
  // State management
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  
  // Filter and pagination states
  const [logType, setLogType] = useState<'security' | 'system'>('security');
  const [eventType, setEventType] = useState('');
  const [userId, setUserId] = useState('');
  const [userIdError, setUserIdError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const [pageSize] = useState(50);

  // Validate MongoDB ObjectId format
  const isValidObjectId = (id: string): boolean => {
    return /^[0-9a-fA-F]{24}$/.test(id);
  };

  // Handle User ID input change with validation
  const handleUserIdChange = (value: string) => {
    setUserId(value);
    setUserIdError(null);
    
    if (value.trim() && !isValidObjectId(value.trim())) {
      setUserIdError('User ID must be a valid MongoDB ObjectId (24 characters, a-f, 0-9)');
    }
  };

  // Fetch activity logs
  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const filters: any = {
        logType,
        page: currentPage,
        limit: pageSize
      };
      
      if (eventType.trim()) filters.eventType = eventType.trim();
      // Only include userId if it's valid and not empty
      if (userId.trim() && isValidObjectId(userId.trim())) {
        filters.userId = userId.trim();
      }
      if (startDate) filters.startDate = startDate;
      if (endDate) filters.endDate = endDate;

      const response: ActivityLogsResponse = await AdminService.getActivityLogs(filters);
      
      // Ensure we have a valid response structure
      if (response && response.success) {
        // Ensure response.data is an array, fallback to empty array if not
        const logsData = Array.isArray(response.data.logs) ? response.data.logs : [];
        setLogs(logsData);
        
        // Safely access pagination data with fallbacks
        if (response.data.pagination) {
          setTotalPages(response.data.pagination.pages || 1);
          setTotalLogs(response.data.pagination.total || 0);
        } else {
          setTotalPages(1);
          setTotalLogs(0);
        }
      } else {
        // Handle API error response
        setLogs([]); // Ensure logs is always an array
        setTotalPages(1);
        setTotalLogs(0);
        throw new Error(response?.message || 'Failed to load activity logs');
      }
    } catch (err) {
      console.error('Error fetching activity logs:', err);
      setError(err instanceof Error ? err.message : 'Failed to load activity logs');
      // Always ensure logs is an empty array on error to prevent map errors
      setLogs([]);
      setTotalPages(1);
      setTotalLogs(0);
    } finally {
      setLoading(false);
    }
  }, [logType, eventType, userId, startDate, endDate, currentPage, pageSize, isValidObjectId]);

  // Export logs as CSV
  const handleExport = async () => {
    try {
      setExporting(true);
      
      const filters: any = { logType };
      if (eventType.trim()) filters.eventType = eventType.trim();
      // Only include userId if it's valid and not empty
      if (userId.trim() && isValidObjectId(userId.trim())) {
        filters.userId = userId.trim();
      }
      if (startDate) filters.startDate = startDate;
      if (endDate) filters.endDate = endDate;

      const csvBlob = await AdminService.exportActivityLogs(filters);
      
      // Create download link
      const url = window.URL.createObjectURL(csvBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `activity-logs-${logType}-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (err) {
      console.error('Error exporting logs:', err);
      alert(err instanceof Error ? err.message : 'Failed to export logs');
    } finally {
      setExporting(false);
    }
  };

  // Effect to fetch data when filters or page change
  useEffect(() => {
    fetchLogs();
  }, [logType, eventType, userId, startDate, endDate, currentPage, fetchLogs]);

  // Format timestamp for display
  const formatTimestamp = (timestamp: string): string => {
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Get event type badge styling
  const getEventTypeBadge = (eventType: string, logType: string) => {
    let badgeClass = 'badge ';
    
    if (logType === 'security') {
      switch (eventType) {
        case 'login':
        case 'logout':
          badgeClass += 'bg-info';
          break;
        case 'failed_login':
        case 'unauthorized_access':
          badgeClass += 'bg-danger';
          break;
        case 'password_change':
        case 'email_change':
          badgeClass += 'bg-warning text-dark';
          break;
        default:
          badgeClass += 'bg-secondary';
      }
    } else { // system logs
      switch (eventType) {
        case 'user_created':
        case 'display_registered':
          badgeClass += 'bg-success';
          break;
        case 'note_moderated':
        case 'user_updated':
          badgeClass += 'bg-primary';
          break;
        case 'error':
        case 'warning':
          badgeClass += 'bg-danger';
          break;
        default:
          badgeClass += 'bg-secondary';
      }
    }
    
    return <span className={badgeClass}>{eventType}</span>;
  };

  // Get truncated metadata display
  const formatMetadata = (metadata: any): string => {
    if (!metadata) return '';
    
    try {
      const metadataStr = JSON.stringify(metadata);
      return metadataStr.length > 100 ? metadataStr.substring(0, 100) + '...' : metadataStr;
    } catch {
      return String(metadata);
    }
  };

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h1 className="h3 mb-1">Activity Logs</h1>
              <p className="text-muted mb-0">
                Monitor system and security events
              </p>
            </div>
            <button 
              className="btn btn-success"
              onClick={handleExport}
              disabled={exporting}
            >
              {exporting ? (
                <>
                  <i className="fas fa-spinner fa-spin me-2"></i>
                  Exporting...
                </>
              ) : (
                <>
                  <i className="fas fa-download me-2"></i>
                  Export CSV
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Filters Row */}
      <div className="row mb-4">
        <div className="col-md-2">
          <label htmlFor="logType" className="form-label">Log Type</label>
          <select
            id="logType"
            className="form-select"
            value={logType}
            onChange={(e) => setLogType(e.target.value as 'security' | 'system')}
          >
            <option value="security">Security</option>
            <option value="system">System</option>
          </select>
        </div>
        
        <div className="col-md-2">
          <label htmlFor="eventType" className="form-label">Event Type</label>
          <input
            type="text"
            id="eventType"
            className="form-control"
            placeholder="Event type..."
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
          />
        </div>

        <div className="col-md-2">
          <label htmlFor="userId" className="form-label">User ID</label>
          <div className="input-group">
            <input
              type="text"
              id="userId"
              className={`form-control ${userIdError ? 'is-invalid' : ''}`}
              placeholder="MongoDB ObjectId..."
              value={userId}
              onChange={(e) => handleUserIdChange(e.target.value)}
            />
            {userId && (
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={() => handleUserIdChange('')}
                title="Clear User ID filter"
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
          {userIdError && (
            <div className="invalid-feedback">
              {userIdError}
            </div>
          )}
          <div className="form-text">
            <small>24-character hex string (e.g., 507f1f77bcf86cd799439011)</small>
          </div>
        </div>

        <div className="col-md-2">
          <label htmlFor="startDate" className="form-label">Start Date</label>
          <input
            type="date"
            id="startDate"
            className="form-control"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        <div className="col-md-2">
          <label htmlFor="endDate" className="form-label">End Date</label>
          <input
            type="date"
            id="endDate"
            className="form-control"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        <div className="col-md-2 d-flex align-items-end">
          <button className="btn btn-outline-secondary w-100" onClick={fetchLogs}>
            <i className="fas fa-sync-alt me-2"></i>
            Refresh
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="row mb-3">
        <div className="col-12">
          <div className="text-muted">
            Showing {logs.length} of {totalLogs} {logType} logs
            {userId && isValidObjectId(userId) && (
              <span className="ms-2">
                <i className="fas fa-filter me-1"></i>
                Filtered by User ID: {userId}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading activity logs...</p>
        </div>
      ) : (
        <>
          {/* Logs Table */}
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-striped table-hover">
                      <thead className="table-dark">
                        <tr>
                          <th>Timestamp</th>
                          <th>Event</th>
                          <th>User</th>
                          <th>Description</th>
                          <th>IP Address</th>
                          <th>Metadata</th>
                        </tr>
                      </thead>
                      <tbody>
                        {logs.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="text-center py-4">
                              No logs found matching current filters.
                            </td>
                          </tr>
                        ) : (
                          logs.map((log) => (
                            <tr key={log._id}>
                              <td>
                                <small>{formatTimestamp(log.timestamp)}</small>
                              </td>
                              <td>
                                {getEventTypeBadge(log.eventType, log.logType)}
                              </td>
                              <td>
                                {log.userEmail ? (
                                  <div>
                                    <div className="fw-bold">{log.userEmail}</div>
                                    {log.userId && (
                                      <small className="text-muted">{log.userId}</small>
                                    )}
                                  </div>
                                ) : log.userId ? (
                                  <small className="text-muted">{log.userId}</small>
                                ) : (
                                  <span className="text-muted">System</span>
                                )}
                              </td>
                              <td>
                                <div className="text-break">
                                  {log.description}
                                </div>
                              </td>
                              <td>
                                {log.ipAddress ? (
                                  <small className="font-monospace">{log.ipAddress}</small>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                              <td>
                                {log.metadata ? (
                                  <small className="text-muted font-monospace">
                                    {formatMetadata(log.metadata)}
                                  </small>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <nav aria-label="Activity logs pagination" className="mt-4">
              <ul className="pagination justify-content-center">
                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                </li>
                
                {/* Page numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                  return (
                    <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    </li>
                  );
                })}
                
                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </>
      )}

      {/* Event Type Quick Filters */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h6 className="mb-0">Quick Filters</h6>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <h6>Security Events</h6>
                  <div className="d-flex flex-wrap gap-2">
                    <button 
                      className="btn btn-sm btn-outline-primary" 
                      onClick={() => {setLogType('security'); setEventType('login');}}
                    >
                      Logins
                    </button>
                    <button 
                      className="btn btn-sm btn-outline-danger" 
                      onClick={() => {setLogType('security'); setEventType('failed_login');}}
                    >
                      Failed Logins
                    </button>
                    <button 
                      className="btn btn-sm btn-outline-warning" 
                      onClick={() => {setLogType('security'); setEventType('password_change');}}
                    >
                      Password Changes
                    </button>
                    <button 
                      className="btn btn-sm btn-outline-danger" 
                      onClick={() => {setLogType('security'); setEventType('unauthorized_access');}}
                    >
                      Unauthorized Access
                    </button>
                  </div>
                </div>
                <div className="col-md-6">
                  <h6>System Events</h6>
                  <div className="d-flex flex-wrap gap-2">
                    <button 
                      className="btn btn-sm btn-outline-success" 
                      onClick={() => {setLogType('system'); setEventType('user_created');}}
                    >
                      User Created
                    </button>
                    <button 
                      className="btn btn-sm btn-outline-primary" 
                      onClick={() => {setLogType('system'); setEventType('note_moderated');}}
                    >
                      Note Moderated
                    </button>
                    <button 
                      className="btn btn-sm btn-outline-success" 
                      onClick={() => {setLogType('system'); setEventType('display_registered');}}
                    >
                      Display Registered
                    </button>
                    <button 
                      className="btn btn-sm btn-outline-danger" 
                      onClick={() => {setLogType('system'); setEventType('error');}}
                    >
                      Errors
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Sample User IDs */}
              {logs.length > 0 && (
                <div className="row mt-3">
                  <div className="col-12">
                    <h6>Sample User IDs from Current Logs</h6>
                    <div className="d-flex flex-wrap gap-2">
                      {Array.from(new Set(logs.filter(log => log.userId).map(log => log.userId).slice(0, 3)))
                        .map(userId => (
                          <button 
                            key={userId}
                            className="btn btn-sm btn-outline-info" 
                            onClick={() => handleUserIdChange(userId!)}
                            title={`Click to filter by this User ID`}
                          >
                            <i className="fas fa-user me-1"></i>
                            {userId}
                          </button>
                        ))}
                    </div>
                    <small className="text-muted">
                      <i className="fas fa-info-circle me-1"></i>
                      Click any sample User ID above to auto-fill the filter
                    </small>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivityLogs; 